import { TextField } from 'mui-rff';
import React, { useState } from 'react';
import { z } from 'zod';

import Display from '@dashboard/components/Display';
import BaseWpForm from '@dashboard/components/form/Form';
import KeyChip from '@dashboard/components/KeyChip';
import { ExpandLessOutlined, ExpandMoreOutlined } from '@mui/icons-material';
import { Box, Button, DialogActions, Link, Stack, Typography } from '@mui/material';
import { alertError } from '@wp-react/notifications';

const FORM_ID = 'unsubscribeGroupForm';

type FormActionsProps = {
  version: 'EDIT' | 'CREATE';
  loading?: boolean;
  onClose: () => void;
};
export function FormActions({ version, loading, onClose }: FormActionsProps) {
  const renderSubmitButtonText = () => {
    switch (version) {
      case 'EDIT':
        return 'Save';
      case 'CREATE':
        return 'Add';
    }
  };
  return (
    <DialogActions>
      <Button disabled={loading} color="secondary" onClick={onClose}>
        Cancel
      </Button>
      <Button disabled={loading} variant="contained" type="submit" form={FORM_ID}>
        {renderSubmitButtonText()}
      </Button>
    </DialogActions>
  );
}

function zOptionalString(message: string) {
  return z
    .string()
    .optional()
    .transform((v) => v?.trim() ?? '')
    .refine((v) => v.length > 0, { message });
}

const FORM_SCHEMA = z.object({
  name: zOptionalString('Content is required'),
  key: z.string().trim().nullable(),
  unsubscribeLinkTemplate: zOptionalString('Content is required'),
  unsubscribePageTitleTemplate: zOptionalString('Content is required'),
  unsubscribePageSubtitleTemplate: zOptionalString('Content is required'),
  unsubscribePageEmailInputLabelTemplate: zOptionalString('Content is required'),
  unsubscribePageSubmitButtonLabelTemplate: zOptionalString('Content is required'),
  unsubscribePageConfirmationMessageTemplate: zOptionalString('Content is required'),
  unsubscribePageInvalidEmailAddressMessageTemplate: zOptionalString('Content is required'),
});

type TFormErrors = {
  name?: string;
  key?: string;
  unsubscribeLinkTemplate?: string;
  unsubscribePageTitleTemplate?: string;
  unsubscribePageSubtitleTemplate?: string;
  unsubscribePageEmailInputLabelTemplate?: string;
  unsubscribePageSubmitButtonLabelTemplate?: string;
  unsubscribePageConfirmationMessageTemplate?: string;
  unsubscribePageInvalidEmailAddressMessageTemplate?: string;
};

export type TFormValue = z.infer<typeof FORM_SCHEMA>;

type TRenderInputProps = {
  autoFocus?: boolean;
  required?: boolean;
  helperText?: string;
  placeholder?: string;
};

type FormProps = {
  initialValues: TFormValue;
  onSubmit: (formValue: TFormValue) => Promise<void>;
};
export function FormBody({ initialValues, onSubmit }: FormProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [errors, setErrors] = useState<TFormErrors>({});

  const handleSubmit = async (formValue: unknown) => {
    setErrors({});
    const result = FORM_SCHEMA.safeParse(formValue);
    if (result.success) {
      await onSubmit(result.data);
      return;
    }
    const formErrors: TFormErrors = {};
    result.error.issues.forEach((issue) => {
      if (issue.path.length > 0) {
        const key = issue.path[0] as keyof TFormErrors;
        formErrors[key] = issue.message;
      }
    });
    alertError('There are errors in the form');
    setErrors(formErrors);
  };

  const renderInput = (label: string, name: keyof TFormValue, { helperText, ...props }: TRenderInputProps) => (
    <TextField
      {...props}
      label={label}
      name={name}
      margin="normal"
      error={Boolean(errors[name])}
      helperText={errors[name] ?? helperText}
    />
  );

  return (
    <BaseWpForm id="unsubscribeGroupForm" initialValues={initialValues} onSubmit={handleSubmit}>
      <Stack spacing={3} mb={3}>
        <Typography color="text.secondary" paragraph>
          Unsubscribe groups allow you to append an unsubscribe link to a related set of emails.{' '}
          <Link target="_blank" href="https://www.usewaypoint.com/docs/unsubscribe-groups">
            Learn more
          </Link>
          .
        </Typography>
        {renderInput('Group name', 'name', {
          autoFocus: true,
          required: true,
          placeholder: 'Beta updates',
          helperText: 'This group name will be shown on the unsubscribe page.',
        })}
        {renderInput('Custom ID key', 'key', {
          placeholder: 'beta-updates',
          helperText: 'Optional descriptive key to use on API calls instead of a generated ID.',
        })}

        <Display display={!isExpanded}>
          <Box>
            <Button onClick={() => setIsExpanded(true)} startIcon={<ExpandMoreOutlined />}>
              Customize unsubscribe link and page
            </Button>
          </Box>
        </Display>
        <Display display={isExpanded}>
          <Stack spacing={1}>
            <Box>
              <Button onClick={() => setIsExpanded(false)} startIcon={<ExpandLessOutlined />}>
                Hide customization options
              </Button>
            </Box>

            <Stack spacing={3} bgcolor="cadet.100" border={1} borderColor="cadet.300" p={3} borderRadius={1}>
              <Stack spacing={0.5}>
                <Typography variant="h6">Email template footer</Typography>
                <Typography variant="body2" color="text.secondary">
                  Available variables: <KeyChip label="{{unsubscribeUrl}}" size="sm" /> &middot;{' '}
                  <Link
                    href="https://www.markdownguide.org/basic-syntax/"
                    target="_blank"
                    color="inherit"
                    underline="always"
                  >
                    Markdown is supported
                  </Link>
                </Typography>
              </Stack>

              {renderInput('Unsubscribe link content', 'unsubscribeLinkTemplate', {})}

              <Stack spacing={0.5}>
                <Typography variant="h6">Unsubscribe page content</Typography>
                <Typography variant="caption" color="text.secondary">
                  Available variables: <KeyChip label="{{groupName}}" size="sm" />
                </Typography>
              </Stack>
              {renderInput('Page title', 'unsubscribePageTitleTemplate', {})}
              {renderInput('Page subtitle', 'unsubscribePageSubtitleTemplate', {})}
              {renderInput('Email input label', 'unsubscribePageEmailInputLabelTemplate', {})}
              {renderInput('Submit button label', 'unsubscribePageSubmitButtonLabelTemplate', {})}
              {renderInput('Success alert message', 'unsubscribePageConfirmationMessageTemplate', {})}
              {renderInput('Invalid email alert message', 'unsubscribePageInvalidEmailAddressMessageTemplate', {})}
            </Stack>
          </Stack>
        </Display>
      </Stack>
    </BaseWpForm>
  );
}
